
import React, { useState } from 'react';
import { X, Menu, GitBranch, BookOpen, Home, Map, Database, Settings, Layers, BookX, Star, GraduationCap, ChevronDown, LogOut, User as UserIcon, Edit2, Lock, Save, LayoutDashboard, Clock } from 'lucide-react';
import { Folder, User } from '../data/types';

export const APP_VERSION = "3.1.0";

export const CHANGELOG = [
    {
        version: "3.1.0",
        date: "2026-01-01",
        title: "品牌重塑與介面更新",
        details: [
            "系統正式更名為「全方位複習系統」",
            "更新版權年份至 2026",
            "優化登入頁面與側邊欄視覺體驗"
        ]
    },
    {
        version: "3.0.0",
        date: "2024-05-23",
        title: "FSRS 智慧複習系統",
        details: [
            "整合 FSRS (Free Spaced Repetition Scheduler) 演算法",
            "新增錯題本智慧複習排程",
            "資料庫結構升級至 v3"
        ]
    },
    {
        version: "2.10.0",
        date: "2024-04-15",
        title: "架構重構",
        details: [
            "React 元件模組化拆分",
            "新增卡片瀏覽手寫塗鴉功能"
        ]
    }
];

export const AVATAR_OPTIONS = [
    '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', 
    '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', '🐺'
];

interface ModalProps {
  isOpen: boolean;
  title: string;
  children?: React.ReactNode;
  onConfirm?: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  isDanger?: boolean;
  showConfirm?: boolean;
}

export const Modal = ({ isOpen, title, children, onConfirm, onCancel, confirmText = "確定", cancelText = "取消", isDanger = false, showConfirm = true }: ModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all scale-100">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <h3 className="font-bold text-lg text-gray-800">{title}</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          {children}
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 font-medium transition-colors"
          >
            {cancelText}
          </button>
          {showConfirm && onConfirm && (
            <button
              onClick={onConfirm}
              className={`px-4 py-2 text-white rounded-lg font-bold shadow-sm transition-colors ${
                isDanger ? 'bg-red-600 hover:bg-red-700' : 'bg-fudan-blue hover:bg-blue-800'
              }`}
            >
              {confirmText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const Header = ({ title, onMenuClick }: { title: string, onMenuClick: () => void }) => (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sticky top-0 z-30 shadow-sm">
        <div className="flex items-center">
            <button 
                onClick={onMenuClick}
                className="p-2 -ml-2 mr-2 text-gray-600 hover:bg-gray-100 rounded-full lg:hidden"
            >
                <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-fudan-blue font-serif tracking-wide">{title}</h1>
        </div>
        <div className="flex items-center space-x-3">
             <div className="hidden md:flex items-center px-3 py-1 bg-blue-50 text-fudan-blue rounded-full text-xs font-bold border border-blue-100">
                 <GitBranch className="w-3 h-3 mr-1" />
                 v{APP_VERSION}
             </div>
        </div>
    </header>
);

const ProfileEditModal = ({ isOpen, user, onCancel, onSave }: { isOpen: boolean, user: User | null, onCancel: () => void, onSave: (u: User) => void }) => {
    const [name, setName] = useState(user?.name || '');
    const [avatar, setAvatar] = useState(user?.avatar || '🎓');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');

    React.useEffect(() => {
        if (isOpen && user) {
            setName(user.name);
            setAvatar(user.avatar || '🎓');
            setNewPassword('');
            setConfirmPassword('');
            setError('');
        }
    }, [isOpen, user]);

    const handleSave = () => {
        if (!user) return;
        if (!name.trim()) {
            setError('請輸入顯示名稱');
            return;
        }
        if (newPassword && newPassword !== confirmPassword) {
            setError('兩次密碼輸入不一致');
            return;
        }

        const updatedUser = { ...user, name, avatar };
        if (newPassword) {
            updatedUser.password = btoa(newPassword);
        }
        onSave(updatedUser);
    };

    return (
        <Modal 
            isOpen={isOpen} 
            title="編輯個人檔案" 
            onCancel={onCancel}
            onConfirm={handleSave}
            confirmText="儲存變更"
        >
            <div className="space-y-4">
                {error && <div className="text-red-600 text-sm font-bold bg-red-50 p-2 rounded">{error}</div>}
                
                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">選擇頭像</label>
                    <div className="grid grid-cols-6 gap-2 bg-gray-50 p-3 rounded-lg max-h-40 overflow-y-auto border border-gray-200">
                        {AVATAR_OPTIONS.map(avi => (
                            <button
                                key={avi}
                                onClick={() => setAvatar(avi)}
                                className={`text-2xl p-1 rounded hover:bg-white hover:shadow-sm transition-all ${avatar === avi ? 'bg-white ring-2 ring-fudan-blue shadow-sm' : ''}`}
                            >
                                {avi}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">顯示名稱</label>
                    <input 
                        type="text" 
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-fudan-blue outline-none"
                    />
                </div>

                <div className="pt-2 border-t border-gray-100">
                    <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                        <Lock className="w-4 h-4 mr-1" /> 修改密碼 (留空則不修改)
                    </label>
                    <input 
                        type="password" 
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="新密碼"
                        className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-fudan-blue outline-none mb-2"
                    />
                    <input 
                        type="password" 
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="確認新密碼"
                        className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-fudan-blue outline-none"
                    />
                </div>
            </div>
        </Modal>
    );
};

export const Sidebar = ({ isOpen, setIsOpen, currentView, setCurrentView, folders, activeGradeId, setActiveGradeId, currentUser, onLogout, onUpdateUser }: any) => {
  const gradeFolders = folders ? folders.filter((f: Folder) => f.parentId === null) : [];
  
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const handleUpdateProfile = (updatedUser: User) => {
      onUpdateUser(updatedUser);
      setIsProfileModalOpen(false);
  };

  return (
  <>
    {isOpen && (
      <div 
        className="fixed inset-0 bg-black/50 z-40 transition-opacity lg:hidden"
        onClick={() => setIsOpen(false)}
      />
    )}
    
    <ProfileEditModal 
        isOpen={isProfileModalOpen} 
        user={currentUser} 
        onCancel={() => setIsProfileModalOpen(false)}
        onSave={handleUpdateProfile}
    />

    <div className={`fixed top-0 left-0 h-full w-64 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:shadow-none border-r border-gray-200 ${isOpen ? 'translate-x-0' : '-translate-x-full'} flex flex-col`}>
      <div className="h-16 flex items-center px-6 bg-fudan-blue text-white shadow-md shrink-0">
          <div className="flex items-center space-x-2">
            <BookOpen className="w-6 h-6" />
            <span className="text-xl font-bold font-serif">全方位複習系統</span>
          </div>
          <button onClick={() => setIsOpen(false)} className="lg:hidden hover:bg-blue-800 p-1 rounded ml-auto">
              <X className="w-6 h-6" />
          </button>
      </div>

      {/* User Info Card - Click to Edit */}
      <div className="p-4 bg-fudan-blue border-t border-blue-800">
        <button 
            onClick={() => setIsProfileModalOpen(true)}
            className="flex items-center w-full p-3 bg-blue-800/50 rounded-lg hover:bg-blue-700/50 transition-colors group relative"
            title="點擊編輯個人檔案"
        >
            <div className="w-10 h-10 rounded-full bg-white text-fudan-blue flex items-center justify-center font-bold text-2xl border-2 border-blue-300">
                {currentUser?.avatar || '🎓'}
            </div>
            <div className="ml-3 overflow-hidden text-left flex-1">
                <p className="text-white text-sm font-bold truncate flex items-center">
                    {currentUser?.name}
                    <Edit2 className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </p>
                <p className="text-blue-300 text-xs truncate">@{currentUser?.username}</p>
            </div>
        </button>
      </div>

      {/* Grade Selector */}
      <div className="px-4 py-2 bg-fudan-blue pb-4">
          <label className="text-xs text-blue-200 font-bold mb-1 block">目前題庫 / 年級</label>
          <div className="relative">
              <select
                  value={activeGradeId}
                  onChange={(e) => setActiveGradeId(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 bg-blue-800 border border-blue-700 text-white rounded-lg text-sm font-bold appearance-none cursor-pointer hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                  {gradeFolders.map((grade: Folder) => (
                      <option key={grade.id} value={grade.id}>{grade.name}</option>
                  ))}
              </select>
              <GraduationCap className="absolute left-2.5 top-2.5 w-4 h-4 text-blue-300 pointer-events-none" />
              <ChevronDown className="absolute right-2.5 top-2.5 w-4 h-4 text-blue-300 pointer-events-none" />
          </div>
      </div>

      <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          <button 
              onClick={() => { setCurrentView('dashboard'); setIsOpen(false); }}
              className={`w-full flex items-center p-3 rounded-lg transition-colors ${currentView === 'dashboard' ? 'bg-blue-50 text-fudan-blue font-bold shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
          >
              <LayoutDashboard className="w-5 h-5 mr-3" />
              儀表板
          </button>
          <button 
              onClick={() => { setCurrentView('quiz'); setIsOpen(false); }}
              className={`w-full flex items-center p-3 rounded-lg transition-colors ${currentView === 'quiz' ? 'bg-blue-50 text-fudan-blue font-bold shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
          >
              <Home className="w-5 h-5 mr-3" />
              測驗首頁
          </button>
          <button 
              onClick={() => { setCurrentView('favorites'); setIsOpen(false); }}
              className={`w-full flex items-center p-3 rounded-lg transition-colors ${currentView === 'favorites' ? 'bg-orange-50 text-orange-700 font-bold shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
          >
              <Star className="w-5 h-5 mr-3" />
              我的最愛
          </button>
          <button 
              onClick={() => { setCurrentView('mistake-book'); setIsOpen(false); }}
              className={`w-full flex items-center p-3 rounded-lg transition-colors ${currentView === 'mistake-book' ? 'bg-red-50 text-red-700 font-bold shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
          >
              <BookX className="w-5 h-5 mr-3" />
              錯題本
          </button>
          <button 
              onClick={() => { setCurrentView('card-browser'); setIsOpen(false); }}
              className={`w-full flex items-center p-3 rounded-lg transition-colors ${currentView === 'card-browser' ? 'bg-yellow-50 text-yellow-700 font-bold shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
          >
              <Layers className="w-5 h-5 mr-3" />
              卡片瀏覽
          </button>
          <button 
              onClick={() => { setCurrentView('learning-map'); setIsOpen(false); }}
              className={`w-full flex items-center p-3 rounded-lg transition-colors ${currentView === 'learning-map' ? 'bg-blue-50 text-fudan-blue font-bold shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
          >
              <Map className="w-5 h-5 mr-3" />
              學習地圖
          </button>
          <button 
              onClick={() => { setCurrentView('admin'); setIsOpen(false); }}
              className={`w-full flex items-center p-3 rounded-lg transition-colors ${currentView === 'admin' ? 'bg-blue-50 text-fudan-blue font-bold shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
          >
              <Database className="w-5 h-5 mr-3" />
              題庫管理
          </button>
           <div className="pt-4 mt-4 border-t border-gray-100">
              <button 
                  onClick={() => { setCurrentView('settings'); setIsOpen(false); }}
                  className={`w-full flex items-center p-3 rounded-lg transition-colors ${currentView === 'settings' ? 'bg-blue-50 text-fudan-blue font-bold shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                  <Settings className="w-5 h-5 mr-3" />
                  系統設定
              </button>
              <button 
                  onClick={onLogout}
                  className={`w-full flex items-center p-3 rounded-lg transition-colors text-red-600 hover:bg-red-50`}
              >
                  <LogOut className="w-5 h-5 mr-3" />
                  登出帳號
              </button>
           </div>
      </nav>
      <div className="p-4 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 text-center">
          <p>Version {APP_VERSION}</p>
          <p className="mt-1">© 2026 全方位複習系統</p>
      </div>
    </div>
  </>
  );
};
